/*
 * Location finder and search results
 * Handles autocomplete finder behavior and Google map rendering
 * for the dynamic location search results page.
 * Returns no value; this section only defines helper functions.
 */

// Loops through a node list or array with a simple callback. Returns nothing.
function globalIIDForEach(items, callback) {
  var index = 0;

  for (index = 0; index < items.length; index++) {
    callback(items[index], index);
  }
}

// Escapes a string for safe HTML output. Returns a sanitized string.
function escapeHtml(value) {
  return String(value || '').replace(/[&<>"']/g, function(character) {
    return {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    }[character];
  });
}

// Normalizes a search string for simple matching. Returns a lowercase plain-text string.
function normalizeLocationSearchValue(value) {
  return (value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

// Creates a short debounce wrapper. Returns a function that delays the callback and returns undefined.
function debounceLocationSearch(callback, delay) {
  var timeoutId = null;

  return function() {
    var args = arguments;

    window.clearTimeout(timeoutId);

    timeoutId = window.setTimeout(function() {
      callback.apply(null, args);
    }, delay);
  };
}

// Builds a URL-friendly slug from a city/state label. Returns a slug string.
function slugifyLocationValue(value) {
  return normalizeLocationSearchValue(value).replace(/\s+/g, '-');
}

// Reads the display label from one Google prediction. Returns a plain string label such as "Dallas, TX".
function getLocationPredictionLabel(prediction) {
  if (!prediction) {
    return '';
  }

  if (prediction.text && prediction.text.text) {
    return prediction.text.text;
  }

  if (prediction.mainText && prediction.secondaryText && prediction.mainText.text && prediction.secondaryText.text) {
    return prediction.mainText.text + ', ' + prediction.secondaryText.text;
  }

  if (prediction.mainText && prediction.mainText.text) {
    return prediction.mainText.text;
  }

  return '';
}

// Extracts city, state, postal code, label, and coordinates from place details. Returns a context object.
function getLocationPlaceContext(place) {
  var context = {
    city: '',
    state: '',
    postalCode: '',
    label: '',
    lat: '',
    lng: ''
  };

  if (!place) {
    return context;
  }

  if (place.displayName) {
    context.label = place.displayName;
  }

  if (place.formattedAddress && !context.label) {
    context.label = place.formattedAddress;
  }

  if (place.location) {
    if (typeof place.location.lat === 'function') {
      context.lat = place.location.lat();
    }

    if (typeof place.location.lng === 'function') {
      context.lng = place.location.lng();
    }
  }

  globalIIDForEach(place.addressComponents || [], function(component) {
    if (!component || !component.types) {
      return;
    }

    if (!context.city && (component.types.indexOf('locality') !== -1 || component.types.indexOf('postal_town') !== -1)) {
      context.city = component.longText || component.shortText || '';
    }

    if (!context.city && component.types.indexOf('administrative_area_level_2') !== -1) {
      context.city = component.longText || component.shortText || '';
    }

    if (!context.state && component.types.indexOf('administrative_area_level_1') !== -1) {
      context.state = component.shortText || component.longText || '';
    }

    if (!context.postalCode && component.types.indexOf('postal_code') !== -1) {
      context.postalCode = component.longText || component.shortText || '';
    }
  });

  if (context.city && context.state) {
    context.label = context.city + ', ' + context.state;
  }

  return context;
}

// Builds the destination URL for one selected place. Returns a full URL string with query args for city, state, postal code, label, lat, and lng.
function getLocationSearchUrl(finder, context) {
  var baseUrl = finder.getAttribute('data-base-url') || '';
  var params = new URLSearchParams();

  if (!baseUrl) {
    return '';
  }

  if (context.city) {
    params.set('city', context.city);
  }

  if (context.state) {
    params.set('state', context.state);
  }

  if (context.postalCode) {
    params.set('postal_code', context.postalCode);
  }

  if (context.label) {
    params.set('label', context.label);
  }

  if (context.lat || context.lat === 0) {
    params.set('lat', context.lat);
  }

  if (context.lng || context.lng === 0) {
    params.set('lng', context.lng);
  }

  return baseUrl.replace(/\/?$/, '/?') + params.toString();
}

// Builds a simple numbered marker icon for Google Maps. Returns a marker icon object.
function createLocationSearchPinIcon(number, color) {
  var pinNumber = parseInt(number, 10);
  var label = Number.isFinite(pinNumber) && pinNumber > 0 ? String(pinNumber) : '';
  var fillColor = color || '#0D223B';
  var svg = [
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 40" width="32" height="40">',
    '<path d="M16 0C9.373 0 4 5.373 4 12c0 8.4 12 28 12 28s12-19.6 12-28C28 5.373 22.627 0 16 0z" fill="' + fillColor + '"/>',
    '<circle cx="16" cy="12" r="10" fill="' + fillColor + '"/>',
    '<circle cx="16" cy="12" r="9" fill="#ffffff" opacity=".1"/>',
    '<text x="16" y="16" text-anchor="middle" font-family="Montserrat, Helvetica, Arial, sans-serif" font-size="14" font-weight="700" fill="#fff">' + label + '</text>',
    '</svg>'
  ].join('');

  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
    scaledSize: new google.maps.Size(32, 40),
    anchor: new google.maps.Point(16, 40)
  };
}

// Sets a viewport-based height for the results layout. Returns nothing.
function updateLocationSearchViewportHeight() {
  var layouts = document.querySelectorAll('.global-iid-location-search-page__layout');
  var availableHeight = 420;

  globalIIDForEach(layouts, function(layout) {
    var rect = layout.getBoundingClientRect ? layout.getBoundingClientRect() : null;
    var topOffset = rect ? rect.top : 0;

    availableHeight = Math.max(window.innerHeight - topOffset - 16, 420);
    layout.style.setProperty('--location-search-available-height', availableHeight + 'px');
  });
}

// Debounces the results layout sizing helper. Returns nothing.
var locationSearchViewportResizeTimer = null;
function scheduleLocationSearchViewportHeight() {
  window.clearTimeout(locationSearchViewportResizeTimer);
  locationSearchViewportResizeTimer = window.setTimeout(updateLocationSearchViewportHeight, 50);
}

// Wires up one finder instance. Returns nothing; event listeners and Google setup happen inside.
async function bindLocationFinder(finder) {
  var input = finder.querySelector('.global-iid-location-finder__input');
  var button = finder.querySelector('.global-iid-location-finder__button');
  var list = finder.querySelector('.global-iid-location-finder__list');
  var country = finder.getAttribute('data-country') || 'ca';
  var activeIndex = -1;
  var currentMatches = [];
  var selectedPrediction = null;
  var sessionToken = null;
  var placesLibrary = null;

  if (!input || !button || !list || !window.google || !window.google.maps || !window.google.maps.importLibrary || finder.getAttribute('data-global-iid-bound') === 'true') {
    return;
  }

  finder.setAttribute('data-global-iid-bound', 'true');
  input.disabled = true;
  button.disabled = true;

  try {
    placesLibrary = await window.google.maps.importLibrary('places');
    sessionToken = new placesLibrary.AutocompleteSessionToken();
  } catch (error) {
    finder.setAttribute('data-global-iid-bound', 'false');
    window.console.error('Location search library load error', error);
    return;
  }

  input.disabled = false;
  button.disabled = false;

  // Hides and clears the suggestion list. Returns nothing.
  function closeList() {
    list.hidden = true;
    list.innerHTML = '';
    activeIndex = -1;
  }

  // Stores the active prediction and updates the visible input. Returns nothing.
  function setSelectedPrediction(prediction) {
    selectedPrediction = prediction || null;

    if (selectedPrediction) {
      input.value = getLocationPredictionLabel(selectedPrediction);
    }

    closeList();
  }

  // Fetches Google place details, builds the results URL, and redirects the browser. Returns nothing.
  async function submitSelectedPrediction(prediction) {
    var place = null;
    var context = null;
    var url = '';

    if (!prediction) {
      return;
    }

    button.disabled = true;

    try {
      place = prediction.toPlace();

      await place.fetchFields({
        fields: ['addressComponents', 'displayName', 'formattedAddress', 'location']
      });

      context = getLocationPlaceContext(place);
      url = getLocationSearchUrl(finder, context);

      if (url) {
        window.location.href = url;
      }
    } catch (error) {
      window.console.error('Location search autocomplete error', error);
    } finally {
      button.disabled = false;
    }
  }

  // Renders the dropdown suggestions for the current query. Returns nothing.
  function renderList(matches) {
    currentMatches = matches;

    if (!matches.length) {
      list.innerHTML = '<li class="global-iid-location-finder__empty">No encontramos resultados.</li>';
      list.hidden = false;
      return;
    }

    list.innerHTML = matches.map(function(match, index) {
      var label = getLocationPredictionLabel(match);
      return '<li class="global-iid-location-finder__item"><button type="button" class="global-iid-location-finder__option' + (index === activeIndex ? ' is-active' : '') + '" data-index="' + index + '">' + escapeHtml(label) + '</button></li>';
    }).join('');
    list.hidden = false;
  }

  // Loads Google autocomplete predictions for one query. Returns a promise that resolves to an array of place prediction objects.
  async function fetchMatches(query) {
    var request = null;
    var response = null;

    if (!query) {
      return [];
    }

    request = {
      input: query,
      includedRegionCodes: [country],
      sessionToken: sessionToken
    };

    response = await placesLibrary.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

    if (!response || !response.suggestions) {
      return [];
    }

    return response.suggestions.map(function(item) {
      return item.placePrediction;
    }).filter(function(item) {
      return !!item;
    }).slice(0, 8);
  }

  // Returns the active prediction, or the first visible match if none is highlighted.
  function getPrimaryPrediction() {
    if (selectedPrediction) {
      return selectedPrediction;
    }

    if (activeIndex >= 0 && currentMatches[activeIndex]) {
      return currentMatches[activeIndex];
    }

    return currentMatches.length ? currentMatches[0] : null;
  }

  // Debounces typing before refreshing the suggestion list. Returns nothing.
  var handleInput = debounceLocationSearch(async function(value) {
    var matches = [];

    activeIndex = -1;
    selectedPrediction = null;

    if (!value) {
      closeList();
      return;
    }

    try {
      matches = await fetchMatches(value);
      renderList(matches);
    } catch (error) {
      window.console.error('Location search suggestions error', error);
      closeList();
    }
  }, 250);

  // Submits the selected suggestion or the first visible match. Returns nothing.
  function submitInput() {
    var prediction = getPrimaryPrediction();

    if (prediction) {
      submitSelectedPrediction(prediction);
    }
  }

  input.addEventListener('input', function() {
    handleInput(input.value.trim());
  });

  input.addEventListener('keydown', function(event) {
    if (!currentMatches.length) {
      if (event.key === 'Enter') {
        event.preventDefault();
        submitInput();
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      activeIndex = Math.min(activeIndex + 1, currentMatches.length - 1);
      renderList(currentMatches);
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      activeIndex = Math.max(activeIndex - 1, 0);
      renderList(currentMatches);
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      selectedPrediction = getPrimaryPrediction();
      submitInput();
    }

    if (event.key === 'Escape') {
      closeList();
    }
  });

  button.addEventListener('click', submitInput);

  list.addEventListener('click', function(event) {
    var option = event.target.closest('.global-iid-location-finder__option');

    if (!option) {
      return;
    }

    setSelectedPrediction(currentMatches[parseInt(option.getAttribute('data-index'), 10)]);
  });

  document.addEventListener('click', function(event) {
    if (!finder.contains(event.target)) {
      closeList();
    }
  });
}

// Initializes one Google map on the results page. Returns nothing; it reads the JSON payload and adds markers.
function initLocationSearchMap(mapNode) {
  if (!mapNode || !window.google || !window.google.maps || mapNode.getAttribute('data-global-iid-map-bound') === 'true') {
    return;
  }

  mapNode.setAttribute('data-global-iid-map-bound', 'true');

  var scriptNode = mapNode.parentNode.querySelector('.global-iid-location-search-map-data');
  var payload = scriptNode ? JSON.parse(scriptNode.textContent) : null;

  if (!payload || !payload.locations || !payload.locations.length) {
    return;
  }

  var center = { lat: payload.center.lat, lng: payload.center.lng };
  var map = new google.maps.Map(mapNode, {
    center: center,
    zoom: payload.locations.length > 1 ? 10 : 13,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: true
  });
  var bounds = new google.maps.LatLngBounds();
  var infoWindow = new google.maps.InfoWindow();
  var markers = [];
  var activeMarkerIndex = -1;
  var defaultZoom = payload.locations.length > 1 ? 10 : 13;
  var geocoder = window.google.maps.Geocoder ? new google.maps.Geocoder() : null;
  var mapWrap = mapNode.parentNode;
  var searchAreaButton = document.createElement('button');
  var appliedSearchState = null;
  var initialIdleComplete = false;
  var hasUserMovedMap = false;

  searchAreaButton.type = 'button';
  searchAreaButton.className = 'global-iid-location-search-page__search-area-button';
  searchAreaButton.textContent = 'SEARCH THIS AREA';
  searchAreaButton.hidden = true;
  mapWrap.appendChild(searchAreaButton);

  function getCurrentMapState() {
    var currentCenter = map.getCenter();

    if (!currentCenter) {
      return null;
    }

    return {
      lat: currentCenter.lat(),
      lng: currentCenter.lng(),
      zoom: map.getZoom()
    };
  }

  function mapStateHasChanged(currentState) {
    if (!appliedSearchState || !currentState) {
      return false;
    }

    return Math.abs(currentState.lat - appliedSearchState.lat) > 0.0001 ||
      Math.abs(currentState.lng - appliedSearchState.lng) > 0.0001 ||
      currentState.zoom !== appliedSearchState.zoom;
  }

  function hideSearchAreaButton() {
    searchAreaButton.hidden = true;
  }

  function showSearchAreaButton() {
    searchAreaButton.hidden = false;
  }

  function updateAppliedSearchState() {
    appliedSearchState = getCurrentMapState();
    hideSearchAreaButton();
  }

  function getGeocodeComponent(components, type, useShortName) {
    var index = 0;

    for (index = 0; index < components.length; index++) {
      if (components[index].types && components[index].types.indexOf(type) !== -1) {
        return useShortName ? components[index].short_name : components[index].long_name;
      }
    }

    return '';
  }

  function buildSearchAreaUrl(currentState, geocodeResult) {
    var url = new URL(window.location.href);
    var addressComponents = geocodeResult && geocodeResult.address_components ? geocodeResult.address_components : [];
    var city = getGeocodeComponent(addressComponents, 'locality', false) || getGeocodeComponent(addressComponents, 'postal_town', false) || getGeocodeComponent(addressComponents, 'administrative_area_level_2', false);
    var state = getGeocodeComponent(addressComponents, 'administrative_area_level_1', true);
    var postalCode = getGeocodeComponent(addressComponents, 'postal_code', false);
    var label = '';

    url.searchParams.delete('city');
    url.searchParams.delete('state');
    url.searchParams.delete('postal_code');
    url.searchParams.delete('label');

    if (city) {
      url.searchParams.set('city', city);
    }

    if (state) {
      url.searchParams.set('state', state);
    }

    if (postalCode) {
      url.searchParams.set('postal_code', postalCode);
    }

    if (city && state) {
      label = city + ', ' + state;
    } else if (postalCode) {
      label = postalCode;
    } else if (geocodeResult && geocodeResult.formatted_address) {
      label = geocodeResult.formatted_address;
    }

    if (label) {
      url.searchParams.set('label', label);
    }

    url.searchParams.set('lat', currentState.lat.toFixed(6));
    url.searchParams.set('lng', currentState.lng.toFixed(6));
    url.searchParams.set('zoom', currentState.zoom);

    return url.toString();
  }

  // Highlights one marker and clears the previous active marker. Returns nothing.
  function highlightLocation(index) {
    var marker = markers[index];
    var location = payload.locations[index];
    var activeMarker = markers[activeMarkerIndex];

    if (!marker || !location) {
      return;
    }

    if (activeMarker && activeMarker !== marker) {
      activeMarker.setIcon(createLocationSearchPinIcon(payload.locations[activeMarkerIndex].number));
    }

    marker.setIcon(createLocationSearchPinIcon(location.number, '#69a9dc'));
    activeMarkerIndex = index;
  }

  // Opens the location in the info window and zooms the map to it. Returns nothing.
  function focusLocation(index) {
    var marker = markers[index];
    var location = payload.locations[index];
    var card = cards[index];

    if (!marker || !location) {
      return;
    }

    highlightLocation(index);
    map.panTo(marker.getPosition());

    if (map.getZoom() < defaultZoom) {
      map.setZoom(defaultZoom);
    }

    infoWindow.setContent(location.infoHtml);
    infoWindow.open(map, marker);

    if (card && card.scrollIntoView) {
      card.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  // Closes the active info window and clears marker highlights. Returns nothing.
  function clearActiveLocation() {
    var activeMarker = markers[activeMarkerIndex];

    if (activeMarker && payload.locations[activeMarkerIndex]) {
      activeMarker.setIcon(createLocationSearchPinIcon(payload.locations[activeMarkerIndex].number));
    }

    infoWindow.close();
    activeMarkerIndex = -1;
  }

  payload.locations.forEach(function(location, index) {
    var markerPosition = { lat: location.lat, lng: location.lng };
    var marker = new google.maps.Marker({
      position: markerPosition,
      map: map,
      title: location.title,
      icon: createLocationSearchPinIcon(location.number)
    });

    bounds.extend(markerPosition);
    markers.push(marker);

    marker.addListener('click', function() {
      focusLocation(index);
    });
  });

  if (payload.zoom) {
    map.setCenter(center);
    map.setZoom(payload.zoom);
  } else if (payload.locations.length > 1) {
    map.fitBounds(bounds, 60);
  } else if (payload.locations.length === 1) {
    map.setCenter(bounds.getCenter());
    map.setZoom(defaultZoom);
  }

  mapNode.addEventListener('pointerdown', function() {
    hasUserMovedMap = true;
  });

  mapNode.addEventListener('wheel', function() {
    hasUserMovedMap = true;
  }, { passive: true });

  var cards = document.querySelectorAll('.global-iid-location-card');
  globalIIDForEach(cards, function(cardNode, index) {
    cardNode.addEventListener('mouseenter', function() {
      highlightLocation(index);
    });

    cardNode.addEventListener('mouseleave', function() {
      if (activeMarkerIndex === index) {
        return;
      }

      if (markers[index] && payload.locations[index]) {
        markers[index].setIcon(createLocationSearchPinIcon(payload.locations[index].number));
      }
    });

    cardNode.addEventListener('click', function(event) {
      if (event.target.closest('a, button, select, input, option')) {
        return;
      }

      focusLocation(index);
    });
  });

  infoWindow.addListener('closeclick', clearActiveLocation);

  map.addListener('idle', function() {
    var currentState = getCurrentMapState();

    if (!initialIdleComplete) {
      initialIdleComplete = true;
      updateAppliedSearchState();
      return;
    }

    if (!hasUserMovedMap) {
      hideSearchAreaButton();
      return;
    }

    if (mapStateHasChanged(currentState)) {
      showSearchAreaButton();
      return;
    }

    hideSearchAreaButton();
  });

  searchAreaButton.addEventListener('click', function() {
    var currentState = getCurrentMapState();

    if (!currentState) {
      return;
    }

    searchAreaButton.disabled = true;

    if (!geocoder) {
      window.location.href = buildSearchAreaUrl(currentState, null);
      return;
    }

    geocoder.geocode({ location: { lat: currentState.lat, lng: currentState.lng } }, function(results, status) {
      var firstResult = results && results.length ? results[0] : null;

      if (status !== 'OK') {
        firstResult = null;
      }

      window.location.href = buildSearchAreaUrl(currentState, firstResult);
    });
  });
}

// Initializes finder and map features after Google assets are available. Returns nothing.
function initializeLocationSearchFeatures() {
  var finders = document.querySelectorAll('.global-iid-location-finder');
  var maps = document.querySelectorAll('.global-iid-location-search-page__map');

  globalIIDForEach(finders, function(finder) {
    bindLocationFinder(finder);
  });

  globalIIDForEach(maps, function(mapNode) {
    initLocationSearchMap(mapNode);
  });

  updateLocationSearchViewportHeight();
}

// Waits briefly for Google Maps to load, then initializes the feature. Returns nothing.
function waitForLocationSearchGoogle(retryCount) {
  if (window.google && window.google.maps && window.google.maps.importLibrary) {
    initializeLocationSearchFeatures();
    return;
  }

  if (retryCount >= 60) {
    return;
  }

  window.setTimeout(function() {
    waitForLocationSearchGoogle(retryCount + 1);
  }, 250);
}

document.addEventListener('DOMContentLoaded', function() {
  waitForLocationSearchGoogle(0);
});

window.addEventListener('load', function() {
  waitForLocationSearchGoogle(0);
});

window.addEventListener('resize', scheduleLocationSearchViewportHeight);
